import clsx from "clsx";
import { useMemo, useState } from "react";

import {
  useBulkOrderMutation,
  useOrdersCache,
  useRouteMutation,
} from "@web-app/api";
import { Button, useAlert } from "@web-app/components";
import useDispatchStore from "@web-app/store/dispatchStore";
import { OrderDeliveryType, Route } from "@web-app/types";
import { pluralize } from "@web-app/utils/helpers";

import AssignModal from "./ActionModals/AssignModal";
import DeleteModal from "./ActionModals/DeleteModal";
import SendToModal from "./ActionModals/SendToModal";

export default function BulkActionsTooltip({
  className,
  onSuccess,
  onUnselectAllOrders,
  selectedOrderIds,
  selectedRouteIds,
}: {
  className?: string;
  onSuccess: () => void;
  onUnselectAllOrders: () => void;
  selectedOrderIds: string[];
  selectedRouteIds: string[];
}) {
  const { showAlertAsync } = useAlert();
  const [bulkActionMode, setBulkActionMode] = useState<
    "assign" | "delete" | "send" | null
  >(null);

  const { update: bulkUpdateOrders, remove: bulkDeleteOrders } =
    useBulkOrderMutation();
  const { updateRouteV2 } = useRouteMutation();

  const { getCachedOrders, invalidateOrdersCache } = useOrdersCache();
  const cachedData = getCachedOrders();
  const orders = cachedData?.orders;

  const routesById = useMemo(() => {
    const map = new Map<string, Route>();
    if (!orders) return map;
    for (const order of orders.values()) {
      if (order.route && !map.has(order.route.id)) {
        map.set(order.route.id, order.route);
      }
    }
    return map;
  }, [orders]);

  const {
    isBothIndividualAndRoutedOrderSelected,
    isRoutedOrderSelectedWithoutFullRoute,
    isRoutedOrderWithinRouteSelected,
    isFullRouteSelected,
  } = useMemo(() => {
    if (!orders) {
      return {
        isBothIndividualAndRoutedOrderSelected: false,
        isFullRouteSelected: false,
        isRoutedOrderSelectedWithoutFullRoute: false,
        isRoutedOrderWithinRouteSelected: false,
      };
    }

    let individualOrderCount = 0;
    let routedOrderCount = 0;
    let isRoutedOrderSelectedWithoutFullRoute = false;
    let isRoutedOrderWithinRouteSelected = false;
    const isFullRouteSelected = !!selectedRouteIds.length;

    const selectedRouteIdSet = new Set(selectedRouteIds);
    selectedOrderIds.forEach((orderId) => {
      const order = orders.get(orderId);
      if (order?.type === OrderDeliveryType.INDIVIDUAL_PICKUP_AND_DROPOFF) {
        individualOrderCount++;
      } else {
        routedOrderCount++;
      }
      if (order?.route) {
        isRoutedOrderWithinRouteSelected = true;
      }
      if (!!order?.route && !selectedRouteIdSet.has(order.route.id)) {
        isRoutedOrderSelectedWithoutFullRoute = true;
      }
    });
    const isBothIndividualAndRoutedOrderSelected =
      individualOrderCount > 0 && routedOrderCount > 0;
    return {
      isBothIndividualAndRoutedOrderSelected,
      isFullRouteSelected,
      isRoutedOrderSelectedWithoutFullRoute,
      isRoutedOrderWithinRouteSelected,
    };
  }, [selectedOrderIds, selectedRouteIds, orders]);

  const commonAssigneeId = useMemo(() => {
    if (!orders) return undefined;
    const selectedRouteIdSet = new Set(selectedRouteIds);
    const ids = new Set<string | null>();
    for (const routeId of selectedRouteIds) {
      const route = routesById.get(routeId);
      if (!route) continue;
      ids.add(route.assigneeId ?? null);
      if (ids.size > 1) return undefined;
    }
    for (const orderId of selectedOrderIds) {
      const order = orders.get(orderId);
      if (!order) continue;
      if (order.route && selectedRouteIdSet.has(order.route.id)) continue;
      ids.add(order.assigneeId ?? null);
      if (ids.size > 1) return undefined;
    }
    if (ids.size === 0) return undefined;
    const [only] = ids;
    return only;
  }, [selectedOrderIds, selectedRouteIds, orders, routesById]);

  const onAssign = async (selectedAssigneeId: string | null) => {
    const assigneeIdValue = selectedAssigneeId ?? "";
    const targetAssigneeId = selectedAssigneeId || null;
    const selectedRouteIdSet = new Set(selectedRouteIds);

    const orderIdsOutsideSelectedRoutes = selectedOrderIds.filter((orderId) => {
      const order = orders?.get(orderId);
      if (!order) return true;
      if (order.route && selectedRouteIdSet.has(order.route.id)) return false;
      return (order.assigneeId ?? null) !== targetAssigneeId;
    });

    const promises: Promise<unknown>[] = [];
    for (const routeId of selectedRouteIds) {
      const route = routesById.get(routeId);
      if (!route) continue;
      if ((route.assigneeId ?? null) === targetAssigneeId) continue;
      promises.push(
        updateRouteV2.mutateAsync({
          assigneeId: assigneeIdValue,
          ordersOrder: route.ordersOrder.map((order, index) => ({
            id: order.id,
            index,
            type: "order",
          })),
          routeId,
        }),
      );
    }

    if (orderIdsOutsideSelectedRoutes.length > 0) {
      promises.push(
        bulkUpdateOrders.mutateAsync({
          orderIds: orderIdsOutsideSelectedRoutes,
          updatedValues: {
            assigneeId: assigneeIdValue,
          },
        }),
      );
    }

    if (promises.length === 0) {
      onCloseBulkActionModal();
      return;
    }

    await showAlertAsync(Promise.all(promises), {
      error: {
        message: "Failed to assign orders. Please try again or contact support",
        title: "Error",
        type: "error",
      },
      loading: {
        message: "Bulk assigning orders ...",
        title: "Assigning",
        type: "default",
      },
      success: {
        message: "Successfully assigned orders",
        title: "Success",
        type: "success",
      },
    });
    invalidateOrdersCache();
    onSuccess();
    onCloseBulkActionModal();
  };

  const onSend = async (selectedWorkspaceId: string) => {
    await showAlertAsync(
      bulkUpdateOrders.mutateAsync({
        orderIds: selectedOrderIds,
        updatedValues: {
          connectedWorkspaceId: selectedWorkspaceId,
        },
      }),
      {
        error: {
          message: "Failed to send orders. Please try again or contact support",
          title: "Error",
          type: "error",
        },
        loading: {
          message: "Bulk sending orders ...",
          title: "Sending",
          type: "default",
        },
        success: {
          message: "Successfully sent orders",
          title: "Success",
          type: "success",
        },
      },
    );

    onSuccess();
    onCloseBulkActionModal();
  };

  const onDelete = async () => {
    await showAlertAsync(
      bulkDeleteOrders.mutateAsync({
        orderIds: selectedOrderIds,
      }),
      {
        error: {
          message:
            "Failed to delete orders. Please try again or contact support",
          title: "Error",
          type: "error",
        },
        loading: {
          message: "Bulk deleting orders ...",
          title: "Deleting",
          type: "default",
        },
        success: {
          message: "Successfully deleted orders",
          title: "Success",
          type: "success",
        },
      },
    );
    onSuccess();
    onCloseBulkActionModal();
    useDispatchStore.setState({ selectedSidebarOrderIds: [] });
  };

  const onCloseBulkActionModal = () => {
    setBulkActionMode(null);
  };

  return (
    <>
      <div
        className={clsx(
          "flex w-[450px] 2xl:w-[620px] h-[40px] px-2.5 justify-between gap-3 items-center rounded-lg bg-gray-200",
          className,
        )}
      >
        <div className="flex pl-2.5 h-[25px] justify-center items-center gap-2 rounded-md bg-gray-700">
          <span className="text-gray-50 text-xs">
            {selectedOrderIds.length}{" "}
            {pluralize("order", selectedOrderIds.length)} selected
          </span>
          <Button
            className="bg-gray-700 hover:bg-gray-500 h-full cursor-pointer"
            icon="xMark"
            size="sm"
            onClick={() => {
              onUnselectAllOrders();
            }}
          ></Button>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            className="!h-[28px]"
            color="light"
            icon="user"
            iconClassName="hidden 2xl:flex"
            onClick={() => setBulkActionMode("assign")}
          >
            Assign
          </Button>
          <Button
            size="sm"
            className="!h-[28px]"
            color="light"
            icon="arrowTurnDownRight"
            iconClassName="hidden 2xl:flex"
            onClick={() => setBulkActionMode("send")}
          >
            Send To
          </Button>
          <Button
            size="sm"
            className="!h-[28px]"
            color="light"
            icon="fasTrash"
            iconClassName="hidden 2xl:flex"
            onClick={() => setBulkActionMode("delete")}
          >
            Delete
          </Button>
        </div>
      </div>
      {bulkActionMode === "delete" && (
        <DeleteModal
          onDelete={onDelete}
          onClose={onCloseBulkActionModal}
          isBothIndividualAndRoutedOrderSelected={
            isBothIndividualAndRoutedOrderSelected
          }
          isRoutedOrderWithinRouteSelected={isRoutedOrderWithinRouteSelected}
          isFullRouteSelected={isFullRouteSelected}
        ></DeleteModal>
      )}
      {bulkActionMode === "assign" && (
        <AssignModal
          initialAssigneeId={commonAssigneeId}
          onAssign={onAssign}
          onClose={onCloseBulkActionModal}
          isBothIndividualAndRoutedOrderSelected={
            isBothIndividualAndRoutedOrderSelected
          }
          isRoutedOrderSelectedWithoutFullRoute={
            isRoutedOrderSelectedWithoutFullRoute
          }
        ></AssignModal>
      )}
      {bulkActionMode === "send" && (
        <SendToModal
          onSend={onSend}
          onClose={onCloseBulkActionModal}
          isBothIndividualAndRoutedOrderSelected={
            isBothIndividualAndRoutedOrderSelected
          }
          isRoutedOrderSelectedWithoutFullRoute={
            isRoutedOrderSelectedWithoutFullRoute
          }
        ></SendToModal>
      )}
    </>
  );
}
